/**
 * Nguồn duy nhất tính tổng đơn sau voucher.
 * POS/index.tsx (gửi lên BE) và PaymentFooter.tsx (hiển thị + tiền thối) cùng dùng,
 * nên hai chỗ không thể lệch nhau.
 */

import { VoucherType } from '@tcr-erp/shared';
import type { VoucherValidationResult } from '@/services/promotion.service';
import type { PaymentMethod } from '../types';

export function sumVoucherDiscount(vouchers: Array<{ discountAmount?: number }>): number {
  return vouchers.reduce((sum, v) => sum + (v.discountAmount ?? 0), 0);
}

/**
 * TCR-1298 — có voucher freeship nhập tay đang áp dụng hay không. BE chưa tính
 * được số tiền giảm phí ship cho voucher (`discountAmount` luôn 0, xem
 * `VoucherApplyService.calculateDiscount()` case `FREE_SHIPPING`), nên KHÔNG
 * dùng `sumVoucherDiscount` để suy ra — phải tự nhận biết qua `voucherType`.
 */
export function hasFreeshipVoucher(vouchers: Array<{ voucherType?: string }>): boolean {
  return vouchers.some((v) => v.voucherType === VoucherType.FREE_SHIPPING);
}

export function computeTotalAfterVoucher(
  grandTotal: number,
  voucherDiscount: number,
  tradeDiscount = 0,
): number {
  return Math.max(0, grandTotal - voucherDiscount - tradeDiscount);
}

export interface RevalidatedVoucher {
  code: string;
  label: string;
  discountAmount: number;
  result: VoucherValidationResult;
}

export interface DroppedVoucher {
  code: string;
  reason: string;
}

export interface VoucherPartition {
  kept: Array<{ code: string; label: string; discountAmount: number }>;
  dropped: DroppedVoucher[];
}

/** Chia voucher sau re-validate thành nhóm còn dùng được và nhóm bị rớt. */
export function partitionValidatedVouchers(input: RevalidatedVoucher[]): VoucherPartition {
  const kept: VoucherPartition['kept'] = [];
  const dropped: DroppedVoucher[] = [];

  for (const v of input) {
    if (v.result.valid) {
      kept.push({ code: v.code, label: v.label, discountAmount: v.result.discount_amount ?? 0 });
    } else {
      dropped.push({ code: v.code, reason: v.result.reason ?? 'Không còn hợp lệ' });
    }
  }

  return { kept, dropped };
}

export interface CheckoutAmounts {
  payable: number;
  cashAmount: number;
  nonCashAmount: number;
}

/**
 * ⚠️ KHÔNG CÒN LÀ NGUỒN TIỀN CỦA CHECKOUT — ĐỪNG NỐI LẠI VÀO `handleCheckout`.
 *
 * Công thức ở đây là công thức CŨ: voucher trừ SAU VAT, KHÔNG biết tới phụ thu,
 * và VAT do `computePosTotals` cộng từng dòng (không gộp nhóm thuế suất). Cả ba
 * điểm đều lệch với `computePosMoney` (`@tcr-erp/shared`) — nguồn DUY NHẤT mà
 * màn hình POS, `handleCheckout` và BE (`pos-order.service.ts`) cùng dùng.
 *
 * Nối lại hàm này vào payload sẽ tái tạo đúng lỗi final review C1: FE hiện một
 * số, gửi BE một số khác ⇒ đơn phi tiền mặt bị 400 `PAYMENT_SPLIT_MISMATCH`,
 * đơn tiền mặt thì lệch quỹ đúng bằng phụ thu + VAT của nó.
 *
 * Giữ lại vì bộ test của nó khoá hành vi kẹp trần `nonCashAmount`, luật mà
 * `handleCheckout` vẫn áp (trên `customerPayable` thay vì trên `grandTotal`).
 */
export function computeCheckoutAmounts(
  grandTotal: number,
  voucherDiscount: number,
  paymentMethod: PaymentMethod,
  nonCashAmount?: number,
  tradeDiscount = 0,
): CheckoutAmounts {
  const payable = computeTotalAfterVoucher(grandTotal, voucherDiscount, tradeDiscount);

  if (paymentMethod === 'cash') {
    return { payable, cashAmount: payable, nonCashAmount: 0 };
  }

  // Kẹp trần: nonCashAmount đặt trước khi áp voucher có thể lớn hơn payable
  // sau khi trừ voucher — không để khách trả thừa qua chuyển khoản.
  const resolvedNonCash = Math.min(nonCashAmount ?? payable, payable);
  return {
    payable,
    cashAmount: Math.max(0, payable - resolvedNonCash),
    nonCashAmount: resolvedNonCash,
  };
}
